<template>
  <Dialog
    header="Line Item"
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :modal="true"
    :closable="false"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '100vw' }"
  >
    <div class="grid">
      <!-- Form -->
      <div class="col-12">
        <div class="grid">
          <div class="col-12">
            <div class="field">
              <label class="font-bold block mb-2">Selected Commodity</label>
              <div
                class="p-3 border-round surface-100 border-1 commodity-selector cursor-pointer"
                @click="showCommoditySelectionDialog = true"
              >
                <div v-if="selectedCommodity" class="text-lg">
                  <div class="font-semibold">{{ selectedCommodity.name }}</div>
                  <div class="text-sm text-500">{{ selectedCommodity.category }}</div>
                  <div
                    v-if="selectedCommodity.current_price_per_lb"
                    class="text-sm text-green-600 font-semibold mt-1"
                  >
                    Current Price: {{ formatCurrency(selectedCommodity.current_price_per_lb) }}/lb
                  </div>
                </div>
                <div v-else class="text-500 italic">Click to select a commodity</div>
                <div class="flex justify-content-between align-items-center mt-2">
                  <span class="text-sm text-500">Click to change</span>
                  <i class="pi pi-chevron-right"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <span class="p-float-label">
              <IftaLabel>
                <InputGroup>
                  <InputNumber
                    v-model="currentItem.price_per_lb"
                    inputId="price_input"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    :minFractionDigits="2"
                    class="w-full"
                    inputmode="decimal"
                  />
                  <InputGroupAddon>
                    <Button
                      icon="pi pi-bolt"
                      severity="secondary"
                      size="large"
                      @click="getQuickPriceEstimate"
                      :disabled="isGettingQuickEstimate || !selectedCommodity"
                      :loading="isGettingQuickEstimate"
                      class="h-full px-3"
                    />
                  </InputGroupAddon>
                </InputGroup>
                <label for="price_input">Price $</label>
              </IftaLabel>
            </span>
          </div>

          <!-- Scale Selection (only show if multiple scales) -->
          <div v-if="showScaleSelector" class="col-12">
            <div class="field">
              <label class="font-bold block mb-2">Selected Scale</label>
              <Dropdown
                v-model="selectedScaleId"
                :options="scales"
                optionLabel="name"
                optionValue="id"
                placeholder="Select a scale"
                class="w-full"
              />
            </div>
          </div>

          <div class="col-12">
            <span class="p-float-label">
              <IftaLabel>
                <InputGroup>
                  <InputNumber
                    v-model="currentItem.weight.gross"
                    inputId="gross_input"
                    suffix=" lbs"
                    class="w-full"
                    inputmode="decimal"
                  />
                  <InputGroupAddon>
                    <Button
                      icon="pi pi-arrow-right-arrow-left"
                      severity="secondary"
                      size="large"
                      @click="captureGrossWeightAndCamera"
                      :disabled="isCapturing || !hasScaleConfig"
                      :loading="isCapturing"
                      class="h-full px-3"
                    />
                  </InputGroupAddon>
                </InputGroup>
                <label for="gross_input">Gross Weight (lbs)</label>
              </IftaLabel>
            </span>
            <div v-if="!hasScaleConfig" class="mt-1 text-xs text-500">
              No scales available. Configure devices in Settings.
            </div>
            <div v-else-if="selectedScale" class="mt-1 text-xs text-500">
              Using: {{ selectedScale.name }} ({{ selectedScale.location || 'No location' }})
            </div>
          </div>

          <div class="col-12 mb-3">
            <span class="p-float-label">
              <IftaLabel>
                <InputGroup>
                  <InputNumber
                    v-model="currentItem.weight.tare"
                    inputId="tare_input"
                    suffix=" lbs"
                    class="w-full"
                    inputmode="decimal"
                  />
                  <InputGroupAddon>
                    <Button
                      icon="pi pi-arrow-right-arrow-left"
                      severity="secondary"
                      size="large"
                      @click="captureTareWeight"
                      :disabled="isCapturing || !hasScaleConfig"
                      :loading="isCapturing"
                      class="h-full px-3"
                    />
                  </InputGroupAddon>
                </InputGroup>
                <label for="tare_input">Tare Weight (lbs)</label>
              </IftaLabel>
            </span>
          </div>

          <!-- Net weight as text display -->
          <div class="col-12 field">
            <label class="font-bold block mb-2">Net Weight</label>
            <div class="text-xl">{{ netWeight.toFixed(2) }} lbs</div>
          </div>

          <!-- Line total as text display -->
          <div class="col-12 field">
            <label class="font-bold block mb-2">Line Total</label>
            <div class="text-xl text-green-600 font-bold">
              {{ formatCurrency(lineTotal) }}
            </div>
          </div>

          <div class="col-12 mb-3">
            <span class="p-float-label">
              <Textarea v-model="currentItem.notes" rows="2" class="w-full" />
              <label>Notes</label>
            </span>
          </div>

          <div class="col-12">
            <FileUpload
              mode="basic"
              accept="image/*"
              :maxFileSize="10000000"
              :auto="true"
              @select="onFileSelect"
              chooseLabel="Upload Image"
              class="w-full"
              :disabled="isUploading"
              :pt="{ input: { capture: 'environment' } }"
            />
            <ProgressBar v-if="isUploading" mode="indeterminate" class="mt-2" style="height: 6px" />
            <div v-if="isCapturing" class="mt-2 text-500 flex align-items-center">
              <i class="pi pi-spin pi-spinner mr-2"></i> Capturing weight and image...
            </div>
            <div v-if="imagePreview || currentItem.image_url" class="mt-2">
              <img
                :src="imagePreview || currentItem.image_url || ''"
                class="border-round shadow-2"
                style="max-height: 100px"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-content-end mt-4">
      <Button label="Cancel" icon="pi pi-times" class="p-button-outlined mr-2" @click="cancel" />
      <Button :label="isEditing ? 'Update' : 'Add'" icon="pi pi-check" @click="save" />
    </div>
  </Dialog>

  <!-- Create Commodity Dialog -->
  <Dialog
    header="Create New Commodity"
    v-model:visible="showCreateCommodityDialog"
    :modal="true"
    :closable="false"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '100vw' }"
  >
    <CommodityCreateForm
      :submitted="commoditySubmitted"
      :initial-commodity="newCommodity"
      @update:commodity="newCommodity = $event"
    />
    <div class="flex justify-content-end mt-4">
      <Button
        label="Cancel"
        icon="pi pi-times"
        class="p-button-outlined mr-2"
        @click="showCreateCommodityDialog = false"
      />
      <Button label="Save Commodity" icon="pi pi-check" @click="saveNewCommodity" />
    </div>
  </Dialog>

  <!-- Commodity Selection Dialog -->
  <CommoditySelectionDialog
    v-model:visible="showCommoditySelectionDialog"
    @commodity-selected="onCommoditySelected"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useTransactionStore } from '@/stores/transactionStoreOld'
import { uploadImage, makeUniqueImagePath } from '@/utils/imageService'
import { useDevicesStore } from '@/stores/v2/devices'
import { useAuthStore } from '@/stores/authStore'
import { useTransactionService } from '@/services/transactionService'
import { useCommodityTypeService } from '@/services/commodityTypeService'

import ProgressBar from 'primevue/progressbar'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import InputGroup from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import IftaLabel from 'primevue/iftalabel'
import Textarea from 'primevue/textarea'
import FileUpload from 'primevue/fileupload'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'

import CommodityCreateForm from '@/components/commodity/CommodityCreateForm.vue'
import CommoditySelectionDialog from '@/components/commodity/CommoditySelectionDialog.vue'

const toast = useToast()
const transactionStore = useTransactionStore()
const devicesStore = useDevicesStore()
const authStore = useAuthStore()
const transactionService = useTransactionService()
const commodityTypeService = useCommodityTypeService()

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  commodityTypes: {
    type: Array,
    required: true,
  },
  itemIndex: {
    type: Number,
    default: -1,
  },
})

// Emits
const emit = defineEmits(['update:visible', 'save', 'cancel'])

// Local state
const isUploading = ref(false)
const isCapturing = ref(false)
const isGettingQuickEstimate = ref(false)
const imagePreview = ref<string | null>(null)
const selectedCommodity = ref<any>(null)
const selectedScaleId = ref<string | null>(null)
const showCreateCommodityDialog = ref(false)
const showCommoditySelectionDialog = ref(false)
const commoditySubmitted = ref(false)
const newCommodity = ref({
  name: '',
  description: '',
  category: '',
  current_price_per_lb: 0,
  is_active: true,
  is_favorite: false,
})

// Computed properties
const isEditing = computed(() => props.itemIndex >= 0)

const currentItem = computed({
  get: () => transactionStore.currentItem,
  set: (value) => transactionStore.setCurrentItem(value),
})

const netWeight = computed(() => {
  const gross = Number(currentItem.value.weight?.gross || 0)
  const tare = Number(currentItem.value.weight?.tare || 0)
  return Math.max(0, gross - tare)
})

const lineTotal = computed(() => {
  const price = Number(currentItem.value.price_per_lb || 0)
  return price * netWeight.value
})

// Scale selection and configuration
const scales = computed(() => devicesStore.scales)
const selectedScale = computed(() => {
  if (selectedScaleId.value) {
    return scales.value.find((s) => s.id === selectedScaleId.value) || null
  }
  return devicesStore.defaultScale
})

const showScaleSelector = computed(() => scales.value.length > 1)
const hasScaleConfig = computed(() => !!selectedScale.value)

// Watch for changes to update net weight
watch(
  () => [currentItem.value.weight?.gross, currentItem.value.weight?.tare],
  () => {
    if (currentItem.value.weight) {
      currentItem.value.weight.net = netWeight.value
    }
  },
  { immediate: true },
)

watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible && isEditing.value) {
      // Find the commodity in the commodityTypes array
      const commodity = props.commodityTypes.find(
        (c: any) => c.id === currentItem.value.commodity_type_id,
      )
      if (commodity) {
        selectedCommodity.value = commodity
        console.log('Commodity found in commodityTypes array', commodity)
      } else {
        console.warn(
          'Commodity not found in commodityTypes array',
          currentItem.value.commodity_type_id,
          props.commodityTypes,
        )
      }
    }
  },
  { immediate: true },
)

// Initialize component - no longer needed

// Methods
function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

function onCommoditySelected(commodity: any) {
  currentItem.value.commodity_type_id = commodity.id
  currentItem.value.price_per_lb = commodity.current_price_per_lb
  currentItem.value.name = commodity.name
  selectedCommodity.value = commodity
}

async function onFileSelect(event: any) {
  const file = event.files[0]
  if (!file) return

  isUploading.value = true

  try {
    // Build a unique storage path for this transaction
    const transactionId = transactionStore.draft?.id || transactionStore.currentTransaction?.id
    if (!transactionId) throw new Error('Missing transaction id for image upload')
    const baseDir = `/transaction_records/${transactionId}`
    const path = makeUniqueImagePath(baseDir, file.name)
    const imageUrl = await uploadImage(file, path)

    if (imageUrl) {
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          imagePreview.value = e.target.result as string

          // Update the current item
          currentItem.value = {
            ...currentItem.value,
            image_url: imageUrl,
          }
        }
      }
      reader.readAsDataURL(file)

      toast.add({
        severity: 'success',
        summary: 'Image Uploaded',
        detail: 'Image has been uploaded successfully',
        life: 3000,
      })
    }
  } catch (error) {
    console.error('Error uploading image:', error)
    toast.add({
      severity: 'error',
      summary: 'Upload Failed',
      detail: 'Failed to upload image. Please try again.',
      life: 3000,
    })
  } finally {
    isUploading.value = false
  }
}

async function captureFromScale() {
  if (!hasScaleConfig.value) {
    toast.add({
      severity: 'warn',
      summary: 'No Scale Configured',
      detail: 'Configure devices in Settings.',
      life: 3000,
    })
    return
  }

  isCapturing.value = true
  let retryCount = 0
  const maxRetries = 1

  while (retryCount <= maxRetries) {
    try {
      const result = await devicesStore.captureScaleWithImage(selectedScaleId.value || undefined)

      // Update weight
      currentItem.value = {
        ...currentItem.value,
        weight: {
          ...currentItem.value.weight,
          gross: result.weight.value,
        },
      }

      // Update image if captured
      if (result.image) {
        currentItem.value = {
          ...currentItem.value,
          image_url: result.image.url,
        }
      }

      toast.add({
        severity: 'success',
        summary: 'Weight and Image Captured',
        detail: `Weight: ${result.weight.value} ${result.weight.unit}${result.image ? ' + Image' : ''}. ${
          retryCount > 0 ? `(Retried ${retryCount} times)` : ''
        }`,
        life: 4000,
      })
      return // Success, exit the retry loop
    } catch (error) {
      console.error(`Scale capture error (attempt ${retryCount + 1})`, error)
      retryCount++

      if (retryCount <= maxRetries) {
        const errorMessage = error instanceof Error ? error.message : 'Capture failed'

        // Check if this is a recoverable error
        const isRecoverable =
          errorMessage.includes('timeout') ||
          errorMessage.includes('busy') ||
          errorMessage.includes('unresponsive') ||
          errorMessage.includes('network')

        if (isRecoverable) {
          toast.add({
            severity: 'info',
            summary: 'Retrying Scale Capture',
            detail: `Attempt ${retryCount}/${maxRetries + 1}: ${errorMessage}`,
            life: 2000,
          })

          // Wait before retry with exponential backoff
          await new Promise((resolve) => setTimeout(resolve, 2000 * retryCount))
          continue
        }
      }

      // Final failure
      const errorMessage = error instanceof Error ? error.message : 'Capture failed'
      toast.add({
        severity: 'error',
        summary: 'Scale Capture Failed',
        detail: `${errorMessage} ${retryCount > 1 ? `(after ${retryCount} attempts)` : ''}`,
        life: 5000,
      })

      // Suggest manual entry as alternative
      toast.add({
        severity: 'info',
        summary: 'Alternative Method',
        detail: 'You can enter the weight manually or upload an image using the buttons above.',
        life: 6000,
      })
      break
    } finally {
      isCapturing.value = false
    }
  }
}

async function captureTareWeight() {
  if (!hasScaleConfig.value) {
    toast.add({
      severity: 'warn',
      summary: 'No Scale Configured',
      detail: 'Configure devices in Settings.',
      life: 3000,
    })
    return
  }

  isCapturing.value = true
  let retryCount = 0
  const maxRetries = 2

  while (retryCount <= maxRetries) {
    try {
      const reading = await devicesStore.readScale(selectedScaleId.value || undefined)
      currentItem.value = {
        ...currentItem.value,
        weight: {
          ...currentItem.value.weight,
          tare: reading.value,
        },
      }

      toast.add({
        severity: 'success',
        summary: 'Tare Weight Captured',
        detail: `${reading.value} ${reading.unit} captured from scale. ${
          retryCount > 0 ? `(Retried ${retryCount} times)` : ''
        }`,
        life: 4000,
      })
      return // Success, exit the retry loop
    } catch (error) {
      console.error(`Tare weight capture error (attempt ${retryCount + 1})`, error)
      retryCount++

      if (retryCount <= maxRetries) {
        const errorMessage = error instanceof Error ? error.message : 'Weight read failed'

        // Check if this is a recoverable error
        const isRecoverable =
          errorMessage.includes('timeout') ||
          errorMessage.includes('busy') ||
          errorMessage.includes('unresponsive')

        if (isRecoverable) {
          toast.add({
            severity: 'info',
            summary: 'Retrying Tare Weight Capture',
            detail: `Attempt ${retryCount}/${maxRetries + 1}: ${errorMessage}`,
            life: 2000,
          })

          // Wait before retry with exponential backoff
          await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount))
          continue
        }
      }

      // Final failure
      const errorMessage = error instanceof Error ? error.message : 'Weight read failed'
      toast.add({
        severity: 'error',
        summary: 'Tare Weight Capture Failed',
        detail: `${errorMessage} ${retryCount > 1 ? `(after ${retryCount} attempts)` : ''}`,
        life: 5000,
      })

      // Suggest manual entry as alternative
      if (retryCount >= 2 && errorMessage.includes('timeout')) {
        toast.add({
          severity: 'info',
          summary: 'Alternative Method',
          detail: 'You can also enter the tare weight manually.',
          life: 4000,
        })
      }
      break
    } finally {
      isCapturing.value = false
    }
  }
}

async function captureGrossWeightAndCamera() {
  // Use the combined capture function
  await captureFromScale()
}

async function getQuickPriceEstimate() {
  if (!selectedCommodity.value?.id) {
    toast.add({
      severity: 'warn',
      summary: 'No Commodity Selected',
      detail: 'Please select a commodity first.',
      life: 3000,
    })
    return
  }

  isGettingQuickEstimate.value = true
  try {
    const estimate = await transactionService.getQuickPriceEstimate(selectedCommodity.value.id)

    if (estimate === null) {
      toast.add({
        severity: 'info',
        summary: 'No Recent Data',
        detail: 'No transactions found for this commodity in the last 30 days.',
        life: 4000,
      })
    } else {
      currentItem.value.price_per_lb = estimate
      toast.add({
        severity: 'success',
        summary: 'Price Estimate Applied',
        detail: `Estimated price (best guess): ${formatCurrency(estimate)}/lb`,
        life: 4000,
      })
    }
  } catch (error) {
    console.error('Quick estimate error', error)
    toast.add({
      severity: 'error',
      summary: 'Estimate Failed',
      detail: error instanceof Error ? error.message : 'Failed to get price estimate',
      life: 3000,
    })
  } finally {
    isGettingQuickEstimate.value = false
  }
}

async function saveNewCommodity() {
  commoditySubmitted.value = true

  // Validate required fields
  if (!newCommodity.value.name || !newCommodity.value.category) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill in all required fields',
      life: 3000,
    })
    return
  }

  try {
    // Create commodity using service
    const createdCommodity = await commodityTypeService.createCommodityType(newCommodity.value)

    // Add the new commodity to the local list
    props.commodityTypes.push(createdCommodity)

    // Select the newly created commodity
    currentItem.value.commodity_type_id = createdCommodity.id!
    currentItem.value.price_per_lb = createdCommodity.current_price_per_lb
    selectedCommodity.value = createdCommodity

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Commodity created successfully',
      life: 3000,
    })

    // Close the dialog and reset form
    showCreateCommodityDialog.value = false
    commoditySubmitted.value = false
    newCommodity.value = {
      name: '',
      description: '',
      category: '',
      current_price_per_lb: 0,
      is_active: true,
      is_favorite: false,
    }
  } catch (error) {
    console.error('Error creating commodity:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create commodity',
      life: 3000,
    })
  }
}

function save() {
  // Validate the item
  if (!currentItem.value.commodity_type_id) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please select a commodity type',
      life: 3000,
    })
    return
  }
  currentItem.value.total_price = lineTotal.value
  // Add the commodity name to the item
  if (selectedCommodity.value) {
    currentItem.value.name = selectedCommodity.value.name
  }
  // Save the item
  transactionStore.saveCurrentItem(props.itemIndex)
  emit('update:visible', false)
  emit('save')
  selectedCommodity.value = null
  imagePreview.value = null
}

function cancel() {
  transactionStore.clearCurrentItem()
  selectedCommodity.value = null
  imagePreview.value = null
  emit('update:visible', false)
  emit('cancel')
}
</script>

<style scoped>
.commodity-selector {
  cursor: pointer;
  transition: all 0.2s ease;
}

.commodity-selector:hover {
  background: #f8f9fa;
  border-color: #6c757d;
}
</style>
